import React, { useEffect, useState, useCallback } from 'react';
import { FaEdit, FaTrash } from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Image } from 'react-bootstrap';
import { supabase } from '../supabaseClient';
import '../styles/BusinessInfo.css'

const BusinessInfo = () => {
  const [businessInfo, setBusinessInfo] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(50);
  const [editItem, setEditItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchBusinessInfo = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('businessinfo')
      .select('*')
      .range((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage - 1);

    if (error) {
      console.error(error);
    } else {
      setBusinessInfo(data);
    }
    setLoading(false);
  }, [currentPage]);

  useEffect(() => {
    fetchBusinessInfo();
  }, [fetchBusinessInfo]);

  const deleteItem = async (id) => {
    const { error } = await supabase
      .from('businessinfo')
      .delete()
      .eq('id', id);

    if (error) {
      console.error(error);
    } else {
      fetchBusinessInfo();
    }
  };

  const updateItem = async () => {
    if (editItem) {
      setSaving(true);
      const { error } = await supabase
        .from('businessinfo')
        .update(editItem)
        .eq('id', editItem.id);

      setSaving(false);

      if (error) {
        console.error(error);
      } else {
        fetchBusinessInfo();
        setEditItem(null);
        toast.success('Data successfully updated');
      }
    }
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const filteredBookings = businessInfo.filter(
    (item) =>
      item.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.contact?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="container mb-0 pb-0">
      <h3 className="mb-4">Business Info</h3>
      <input
        type="text"
        placeholder="Search by Business Name or Contact"
        className="form-control"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
      />
      {loading ? (
        <p>Loading...</p>
      ) : (
        <div className="table-responsive" style={{width: '78vw', height: '52vh'}}>
          <table className="table table-hover">
            <thead>
              <tr>
                <th>ID</th>
                <th>Title</th>
                <th>Description</th>
                <th>Image</th>
                <th>Address</th>
                <th>Longitude</th>
                <th>Latitude</th>
                <th>Number of Stars</th>
                <th>Rating</th>
                <th>Gallery</th>
                <th>Working Hour</th>
                <th>About</th>
                <th>Home Service</th>
                <th>On Site</th>
                <th>Workers</th>
                <th>Bank Details</th>
                <th>Contact</th>
                <th>Socials</th>
                <th>Services</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredBookings.map((item) => (
                <tr key={item.id}>
                  <td>{item.id}</td>
                  <td style={{color: 'blue'}}>{item.title}</td>
                  <td>{item.description}</td>
                  <td>
                    <Image src={item.image} alt={item.image} width={70} height={70} rounded />
                  </td>
                  <td className="wide-column">{item.address}</td>
                  <td>{item.longitude}</td>
                  <td>{item.latitude}</td>
                  <td>{item.numberofstars}</td>
                  <td>{item.rating}</td>
                  <td>
                    {item.gallery?.map((galleryItem) => (
                      <div key={galleryItem.id}>
                        <Image src={galleryItem.uri} alt={galleryItem.uri} width={70} height={70} rounded style={{marginBottom: '10px'}} />
                      </div>
                    ))}
                  </td>
                  <td>
                    {item.workingHour?.map((wh, index) => (
                      <div key={index}>
                        <strong className="text-primary">{wh.day}:</strong> {wh.opening} - {wh.closing}
                      </div>
                    ))}
                  </td>
                  <td>{item.about}</td>
                  <td>{item.homeService ? 'Yes' : 'No'}</td>
                  <td>{item.onSite ? 'Yes' : 'No'}</td>
                  <td>
                    {item.workers?.map((worker) => (
                      <div key={worker.id} className="d-flex align-items-center my-2">
                        <Image src={worker.image} alt={worker.name} width={50} height={50} roundedCircle />
                        <div className="ms-2">
                          <div className="fw-bold text-primary">{worker.name}</div>
                          <div>{worker.skills.join(', ')}</div>
                        </div>
                      </div>
                    ))}
                  </td>
                  <td>
                    <p><strong className="text-primary">Bank Name: </strong>{item.bankDetails?.bank_name}</p>
                    <p><strong className="text-primary">Account Name: </strong>{item.bankDetails?.account_name}</p>
                    <p><strong className="text-primary">Account Number: </strong>{item.bankDetails?.account_number}</p>
                  </td>
                  <td>{item.contact}</td>
                  <td>{item.socials}</td>
                  <td>
                    {item.services2 && Object.entries(item.services2).map(([service, available]) => (
                      <div key={service}>{service}</div>
                    ))}
                  </td>
                  <td className="d-flex justify-content-center">
                    <button
                      className="btn btn-primary me-2"
                      onClick={() => setEditItem(item)}
                    >
                      <FaEdit />
                    </button>
                    <button
                      className="btn btn-danger"
                      onClick={() => deleteItem(item.id)}
                    >
                      <FaTrash />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="d-flex justify-content-center mt-4">
        <button
          className="btn btn-secondary me-2"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        <button
          className="btn btn-secondary"
          onClick={() => handlePageChange(currentPage + 1)}
        >
          Next
        </button>
      </div>

      {editItem && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Item</h5>
                <button type="button" className="btn-close" onClick={() => setEditItem(null)}></button>
              </div>
              <div className="modal-body">
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    updateItem();
                  }}
                >
                  <div className="mb-3">
                    <label className="form-label">Title</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editItem.title}
                      onChange={(e) =>
                        setEditItem({ ...editItem, title: e.target.value })
                      }
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-control"
                      value={editItem.description}
                      onChange={(e) =>
                        setEditItem({ ...editItem, description: e.target.value })
                      }
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Image</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editItem.image}
                      onChange={(e) =>
                        setEditItem({ ...editItem, image: e.target.value })
                      }
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Address</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editItem.address}
                      onChange={(e) =>
                        setEditItem({ ...editItem, address: e.target.value })
                      }
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Longitude</label>
                    <input
                      type="number"
                      className="form-control"
                      value={editItem.longitude}
                      onChange={(e) =>
                        setEditItem({ ...editItem, longitude: parseFloat(e.target.value) })
                      }
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Latitude</label>
                    <input
                      type="number"
                      className="form-control"
                      value={editItem.latitude}
                      onChange={(e) =>
                        setEditItem({ ...editItem, latitude: parseFloat(e.target.value) })
                      }
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Number of Stars</label>
                    <input
                      type="number"
                      className="form-control"
                      value={editItem.numberofstars}
                      onChange={(e) =>
                        setEditItem({ ...editItem, numberofstars: parseInt(e.target.value) })
                      }
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Rating</label>
                    <input
                      type="number"
                      className="form-control"
                      value={editItem.rating}
                      onChange={(e) =>
                        setEditItem({ ...editItem, rating: parseFloat(e.target.value) })
                      }
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Gallery</label>
                    <textarea
                      className="form-control"
                      value={editItem.gallery?.map(g => g.uri).join(', ')}
                      onChange={(e) =>
                        setEditItem({ ...editItem, gallery: e.target.value.split(', ').map((uri, index) => ({ id: index.toString(), uri })) })
                      }
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Working Hour</label>
                    <textarea
                      className="form-control"
                      value={editItem.workingHour?.map(wh => `${wh.day}: ${wh.opening} - ${wh.closing}`).join(', ')}
                      onChange={(e) =>
                        setEditItem({ ...editItem, workingHour: e.target.value.split(', ').map((wh) => {
                          const [day, times] = wh.split(': ');
                          const [opening, closing] = times.split(' - ');
                          return { day, opening, closing };
                        }) })
                      }
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">About</label>
                    <textarea
                      className="form-control"
                      value={editItem.about}
                      onChange={(e) =>
                        setEditItem({ ...editItem, about: e.target.value })
                      }
                    />
                  </div>
                  <div className="mb-3 form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={editItem.homeService}
                      onChange={(e) =>
                        setEditItem({ ...editItem, homeService: e.target.checked })
                      }
                    />
                    <label className="form-check-label">Home Service</label>
                  </div>
                  <div className="mb-3 form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={editItem.onSite}
                      onChange={(e) =>
                        setEditItem({ ...editItem, onSite: e.target.checked })
                      }
                    />
                    <label className="form-check-label">On Site</label>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Workers</label>
                    <textarea
                      className="form-control"
                      value={editItem.workers?.map(w => `${w.name}: ${w.skills.join(', ')}`).join(', ')}
                      onChange={(e) =>
                        setEditItem({ ...editItem, workers: e.target.value.split(', ').map((w, index) => {
                          const [name, skills] = w.split(': ');
                          return { id: index.toString(), name, image: '', skills: skills.split(', ') };
                        }) })
                      }
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Bank Details</label>
                    <input
                      type="text"
                      className="form-control"
                      value={`${editItem.bankDetails?.bank_name}, ${editItem.bankDetails?.account_name}, ${editItem.bankDetails?.account_number}`}
                      onChange={(e) => {
                        const [bank_name, account_name, account_number] = e.target.value.split(', ');
                        setEditItem({ ...editItem, bankDetails: { bank_name, account_name, account_number } });
                      }}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Contact</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editItem.contact}
                      onChange={(e) =>
                        setEditItem({ ...editItem, contact: e.target.value })
                      }
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Socials</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editItem.socials}
                      onChange={(e) =>
                        setEditItem({ ...editItem, socials: e.target.value })
                      }
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Services</label>
                    <textarea
                      className="form-control"
                      value={Object.entries(editItem.services2 ?? {}).map(([service, available]) => `${service}: ${available ? 'Yes' : 'No'}`).join(', ')}
                      onChange={(e) => {
                        const services = e.target.value.split(', ').reduce((acc, service) => {
                          const [name, available] = service.split(': ');
                          acc[name] = available === 'Yes';
                          return acc;
                        }, {});
                        setEditItem({ ...editItem, services });
                      }}
                    />
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setEditItem(null)}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={saving}
                    >
                      {saving ? 'Loading...' : 'Save'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
      <ToastContainer />
    </div>
  );
};

export default BusinessInfo;
